import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  TextField,
  Chip,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
// import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import PageLayout from "../../components/layout/PageLayout";
import DashboardDrilldownBanner from "../../components/dashboard/DashboardDrilldownBanner";
import { useDashboardDrilldown } from "../../hooks/useDashboardDrilldown";
import {
  useGetInvoicesQuery,
  useDeleteInvoiceMutation,
  useLazyGetGenerateInvoicePDFQuery,
} from "../../store/api";
import { extractPagination } from "../../utils/response";
import { toast } from "react-toastify";
import Can from "../../components/permissions/Can";
import { SweetAlert } from "../../utils/sweetAlert";
import { buildInvoiceRelationParams } from "./invoiceApiHelpers";
import {
  extractInvoiceLeadServiceNames,
  getInvoiceAmountValue,
  getInvoiceStatusName,
  isInvoiceStatusPartial,
} from "./invoiceFieldParsers";
import InvoiceRecreateInstallmentModal from "./InvoiceRecreateInstallmentModal";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const InvoiceServicesCell = ({ row }) => {
  const names = extractInvoiceLeadServiceNames(row);
  if (names.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        py: 0,
        pl: 2.25,
        listStyleType: "disc",
        "& li": { display: "list-item" },
      }}
    >
      {names.map((name, index) => (
        <Box component="li" key={`${name}-${index}`}>
          <Typography variant="body2">{name}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const formatMoney = (amount, currency) => {
  if (amount === undefined || amount === null || amount === "") return "—";
  const n = Number(amount);
  const sym = currency?.symbol || currency?.code || "";
  if (Number.isNaN(n)) return `${sym} ${amount}`.trim();
  return `${sym ? `${sym} ` : ""}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Hosted pay link — InvoiceResource builds it as `payment_url`. */
const getInvoiceShareUrl = (row) => row?.payment_url ?? "";

const getInvoicePaymentTypeObject = (row) => {
  if (row?.paymentType && typeof row.paymentType === "object")
    return row.paymentType;
  if (row?.payment_type && typeof row.payment_type === "object")
    return row.payment_type;
  if (
    row?.account?.paymentType &&
    typeof row.account.paymentType === "object"
  ) {
    return row.account.paymentType;
  }
  if (
    row?.account?.payment_type &&
    typeof row.account.payment_type === "object"
  ) {
    return row.account.payment_type;
  }
  return null;
};

const getInvoicePaymentTypeLabel = (row) => {
  const pt = getInvoicePaymentTypeObject(row);
  const v =
    pt?.name ??
    pt?.label ??
    (typeof row?.payment_type === "string" ? row.payment_type : null);
  return v != null && String(v).trim() !== "" ? String(v).trim() : "—";
};

/** Match card types across slug/code/name (same idea as CreateInvoice pickCardPaymentType). */
const isInvoicePaymentTypeCard = (row) => {
  const pt = getInvoicePaymentTypeObject(row);
  if (!pt) return false;
  const slug = String(pt.slug ?? pt.code ?? "").toLowerCase();
  if (slug === "card" || slug === "credit_card" || slug === "credit-card")
    return true;
  const name = String(pt.name || "")
    .trim()
    .toLowerCase();
  if (name === "card" || name === "credit card") return true;
  if (/\bcard\b/i.test(String(pt.name || ""))) return true;
  return false;
};

const canCopyInvoiceUrl = (row) =>
  isInvoicePaymentTypeCard(row) &&
  Boolean(String(getInvoiceShareUrl(row) || "").trim());

const statusLabel = (row) => getInvoiceStatusName(row) || "—";

const InvoiceList = () => {
  const navigate = useNavigate();
  const { params: drilldownParams } = useDashboardDrilldown();
  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 10,
    total: 0,
  });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [recreateModalOpen, setRecreateModalOpen] = useState(false);
  const [recreateInvoiceId, setRecreateInvoiceId] = useState(null);

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      per_page: pagination.per_page,
      paginate: 1,
      ...buildInvoiceRelationParams(),
      ...(search ? { search } : {}),
      ...drilldownParams,
    }),
    [pagination.page, pagination.per_page, search, drilldownParams],
  );

  const {
    data,
    isFetching: loading,
    error,
    refetch,
  } = useGetInvoicesQuery(queryParams);
  const [deleteInvoice] = useDeleteInvoiceMutation();

  const [getGenerateInvoicePDF, { isFetching: isGeneratingPdf }] =
    useLazyGetGenerateInvoicePDFQuery();

  const rows = useMemo(() => {
    const list =
      data?.data?.list ??
      data?.list ??
      (Array.isArray(data?.data) ? data.data : []);
    return Array.isArray(list) ? list : [];
  }, [data]);

  useEffect(() => {
    if (!data) return;
    const pag = extractPagination(data);
    setPagination((p) => ({ ...p, total: pag.total ?? 0 }));
  }, [data]);

  useEffect(() => {
    if (error) toast.error(error?.data?.message || "Failed to load invoices");
  }, [error]);

  const handleChangePage = (_, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage + 1 }));
  };

  const handleChangeRowsPerPage = (event) => {
    const per = parseInt(event.target.value, 10) || 10;
    setPagination((prev) => ({ ...prev, page: 1, per_page: per }));
  };

  const handleSearch = () => {
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearch(searchInput.trim());
  };

  const handleOpenMenu = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const goEdit = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/invoices/edit/${id}`);
  };

  const goView = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (id) navigate(`/invoices/view/${id}`);
  };

  const openRecreateInstallment = () => {
    const id = selectedRow?.id;
    handleCloseMenu();
    if (!id) return;
    setRecreateInvoiceId(id);
    setRecreateModalOpen(true);
  };

  const closeRecreateInstallment = () => {
    setRecreateModalOpen(false);
    setRecreateInvoiceId(null);
  };

  // const copyInvoiceUrl = async () => {
  //   const row = selectedRow;
  //   handleCloseMenu();
  //   if (!isInvoicePaymentTypeCard(row)) {
  //     toast.warn("Invoice URL copy is only available for card payment types");
  //     return;
  //   }
  //   const url = getInvoiceShareUrl(row);
  //   if (!url) {
  //     toast.warn("No invoice URL available for this record");
  //     return;
  //   }
  //   try {
  //     await navigator.clipboard.writeText(url);
  //     toast.success("Invoice URL copied to clipboard");
  //   } catch {
  //     toast.error("Could not copy to clipboard");
  //   }
  // };

  const copyInvoiceUrlFromRow = async (e, row) => {
    e.stopPropagation();
    if (!isInvoicePaymentTypeCard(row)) {
      toast.warn("Invoice URL copy is only available for card payment types");
      return;
    }
    const url = getInvoiceShareUrl(row);
    if (!url) {
      toast.warn("No invoice URL available for this record");
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Invoice URL copied to clipboard");
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const handleDelete = async () => {
    const row = selectedRow;
    if (!row?.id) return;
    handleCloseMenu();
    const result = await SweetAlert.confirm({
      title: "Delete invoice?",
      text: "This action cannot be undone.",
      confirmButtonText: "Delete",
      icon: "warning",
    });
    if (!result.isConfirmed) return;
    setActionLoadingId(row.id);
    try {
      await deleteInvoice(row.id).unwrap();
      toast.success("Invoice deleted");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete invoice");
    } finally {
      setActionLoadingId(null);
    }
  };

  const chipColor = (label) => {
    const lower = String(label || "").toLowerCase();
    if (
      ["paid", "completed", "complete", "approved"].some((x) =>
        lower.includes(x),
      )
    )
      return "success";
    if (["pending", "draft", "sent"].some((x) => lower.includes(x)))
      return "warning";
    if (
      ["failed", "cancelled", "canceled", "declined"].some((x) =>
        lower.includes(x),
      )
    )
      return "error";
    return "default";
  };

  const handleGenerateInvoicePDF = async () => {
    const invoiceId = selectedRow?.id;

    if (!invoiceId) {
      toast.error("Invoice ID not found");
      return;
    }

    const pdfWindow = window.open("", "_blank");

    if (!pdfWindow) {
      toast.error("Please allow pop-ups in your browser");
      handleCloseMenu();
      return;
    }

    handleCloseMenu();

    try {
      const data = await getGenerateInvoicePDF(
        { id: invoiceId },
        false,
      ).unwrap();

      const source = data instanceof Blob ? data : new Blob([data]);
      const contentType = String(source.type || "").toLowerCase();

      if (contentType.includes("json") || contentType.startsWith("text/")) {
        const text = await source.text();
        let message = "Failed to generate invoice PDF";
        try {
          message = JSON.parse(text)?.message || message;
        } catch {
          message = text || message;
        }
        throw new Error(message);
      }

      if (!source.size) {
        throw new Error("PDF response is empty");
      }

      const pdfBlob =
        contentType === "application/pdf"
          ? source
          : new Blob([source], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(pdfBlob);
      pdfWindow.location.replace(pdfUrl);
      window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (error) {
      pdfWindow.close();

      let message =
        error?.data?.message ||
        error?.message ||
        "Failed to generate invoice PDF";

      if (error?.data instanceof Blob) {
        try {
          const text = await error.data.text();
          message = JSON.parse(text)?.message || message;
        } catch {
          message = "Failed to generate invoice PDF";
        }
      }

      toast.error(message);
    }
  };

  return (
    <PageLayout>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <DashboardDrilldownBanner />
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography
            variant="h4"
            sx={{ color: "primary.main", fontWeight: 600 }}
          >
            Invoices
          </Typography>
          <Can permission="invoice-create">
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/invoices/create")}
            >
              Add invoice
            </Button>
          </Can>
        </Box>

        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Search invoices..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
            }}
          />
          <Button variant="outlined" onClick={handleSearch}>
            Search
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          >
            Clear
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "grey.100" }}>
                    <TableCell sx={{ fontWeight: 600 }}>Author</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Lead name</TableCell>
                    {/* <TableCell sx={{ fontWeight: 600 }}>Invoice ID</TableCell> */}
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Brand </TableCell>
                    <Can permission="payment-account-list">
                      <TableCell sx={{ fontWeight: 600 }}>Descriptor</TableCell>
                      {/* <TableCell sx={{ fontWeight: 600 }}>
                        Payment Account
                      </TableCell> */}
                    </Can>
                    <TableCell sx={{ fontWeight: 600 }}>Services</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payment type</TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 100, textAlign: "center" }}
                    >
                      Invoice URL
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: 600, width: 72, textAlign: "center" }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No invoices found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const currentDate = new Date();
                      const isExpired = new Date(row.expires_at) < currentDate;
                      return (
                        <TableRow
                          key={row.id}
                          hover
                          onClick={() => {
                            if (row.id) navigate(`/invoices/view/${row.id}`);
                          }}
                          sx={{ cursor: row.id ? "pointer" : "default" }}
                          aria-label="View invoice"
                        >
                          <TableCell>
                            {row.author?.name ?? row.user?.name ?? "—"}
                          </TableCell>
                          <TableCell>{row?.order?.lead?.name ?? "—"}</TableCell>
                          {/* <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                          {displayInvoiceId(row)}
                        </TableCell> */}
                          <TableCell>
                            {formatMoney(
                              getInvoiceAmountValue(row),
                              row.currency,
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {row.brand?.name ?? "—"}
                          </TableCell>
                          <Can permission="payment-account-list">
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.account?.descriptor ?? "—"}
                            </TableCell>
                            {/* <TableCell sx={{ whiteSpace: "nowrap" }}>
                              {row.account?.name ?? "—"}
                            </TableCell> */}
                          </Can>
                          <TableCell
                            sx={{ minWidth: 200, verticalAlign: "top" }}
                          >
                            <InvoiceServicesCell row={row} />
                          </TableCell>
                          <TableCell
                            sx={{ minWidth: 200, verticalAlign: "top" }}
                          >
                            {row.created_at ?? "-"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={statusLabel(row)}
                              color={chipColor(statusLabel(row))}
                              sx={{ textTransform: "capitalize" }}
                            />
                          </TableCell>
                          <TableCell>
                            {getInvoicePaymentTypeLabel(row)}
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textAlign: "center", width: 100 }}
                          >
                            {!isInvoicePaymentTypeCard(row) ? (
                              <Typography
                                color="text.secondary"
                                variant="body2"
                                component="span"
                              >
                                Nil
                              </Typography>
                            ) : (
                              <Can permission="invoice-view">
                                <Tooltip
                                  title={
                                    // getInvoiceShareUrl(row)?.trim()
                                    isExpired
                                      ? "Invoice URL has expired. Please create new invoice."
                                      : "Copy invoice URL"
                                  }
                                  placement="top"
                                  enterDelay={400}
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        copyInvoiceUrlFromRow(e, row);
                                        console.log("e", row);
                                      }}
                                      // onClick={(e) => console.log("e", row)}
                                      disabled={
                                        !canCopyInvoiceUrl(row) || isExpired
                                      }
                                      aria-label="Copy invoice URL"
                                      color="primary"
                                    >
                                      {isExpired ? (
                                        <ReportGmailerrorredIcon
                                          fontSize="small"
                                          sx={{ color: "red" }}
                                        />
                                      ) : (
                                        <ContentCopyIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Can>
                            )}
                          </TableCell>
                          <TableCell
                            onClick={(e) => e.stopPropagation()}
                            sx={{ textAlign: "center" }}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleOpenMenu(e, row)}
                              disabled={actionLoadingId === row.id}
                              aria-label="Actions"
                            >
                              {actionLoadingId === row.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <MoreVertIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && (
              <TablePagination
                component="div"
                count={pagination.total}
                page={pagination.page - 1}
                onPageChange={handleChangePage}
                rowsPerPage={pagination.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Rows per page"
              />
            )}
          </CardContent>
        </Card>
      </Container>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Can permission="invoice-view">
          <MenuItem onClick={goView}>
            <ListItemIcon>
              <VisibilityIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View</ListItemText>
          </MenuItem>
        </Can>
        {selectedRow && isInvoiceStatusPartial(selectedRow) && (
          <Can permission="invoice-recreate-installment-token">
            <MenuItem onClick={openRecreateInstallment}>
              <ListItemIcon>
                <AutorenewIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Recreate installment</ListItemText>
            </MenuItem>
          </Can>
        )}

        <Can permission="invoice-generate-pdf">
          <MenuItem
            onClick={handleGenerateInvoicePDF}
            disabled={!selectedRow?.id || isGeneratingPdf}
          >
            <ListItemIcon>
              {isGeneratingPdf ? (
                <CircularProgress size={18} />
              ) : (
                <PictureAsPdfIcon fontSize="small" />
              )}
            </ListItemIcon>
            <ListItemText>
              {isGeneratingPdf ? "Generating PDF..." : "View Invoice PDF"}
            </ListItemText>
          </MenuItem>
        </Can>
        <Can permission="invoice-edit">
          {selectedRow && !isInvoiceStatusPartial(selectedRow) && (
            <MenuItem onClick={goEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
        </Can>
        <Can permission="invoice-delete">
          <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Can>
      </Menu>

      <InvoiceRecreateInstallmentModal
        open={recreateModalOpen}
        invoiceId={recreateInvoiceId}
        onClose={closeRecreateInstallment}
        onSuccess={refetch}
      />
    </PageLayout>
  );
};

export default InvoiceList;
